import React, { useCallback, useEffect, useState } from 'react';
import { authApi } from '../../services/authApi';

/**
 * Full-screen blocking terms-of-service gate (Copilot clickwrap system).
 *
 * Rendered inside ProtectedRoute, i.e. only for authenticated users. Checks
 * Copilot's /auth/terms-status once per mount; while the user has no recorded
 * acceptance of the CURRENT terms version, a blocking overlay covers the app
 * until they click "I agree" (recorded with surface='notes'). Mirrors the
 * eCW-viewer gate: one click, links to the full documents, and check failures
 * fail CLOSED (locked screen with a retry) — never a silent pass-through.
 */

type GateState =
  | { kind: 'checking' }
  | { kind: 'clear' }
  | { kind: 'required'; version: string; termsUrl: string; baaUrl: string; privacyUrl: string }
  | { kind: 'error'; message: string };

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 100000,
  background: 'rgba(15,23,42,0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
};

const cardStyle: React.CSSProperties = {
  maxWidth: 520,
  width: '100%',
  background: '#fff',
  borderRadius: 8,
  padding: '1.75rem 2rem',
  color: '#334155',
};

const buttonStyle: React.CSSProperties = {
  background: '#1e293b',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '0.55rem 1.5rem',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: '0.95rem',
};

export const TermsGate: React.FC = () => {
  const [state, setState] = useState<GateState>({ kind: 'checking' });
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  const check = useCallback(async () => {
    setState({ kind: 'checking' });
    try {
      const st = await authApi.getTermsStatus();
      if (st.acceptance_required) {
        setState({
          kind: 'required',
          version: st.current_version,
          termsUrl: st.terms_url,
          baaUrl: st.baa_url,
          privacyUrl: st.privacy_url,
        });
      } else {
        setState({ kind: 'clear' });
      }
    } catch (e: any) {
      // 401 is handled by the client interceptor (redirect to /login); any
      // other failure locks the screen — the gate never fails open.
      setState({ kind: 'error', message: e?.message || 'terms check failed' });
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const accept = async () => {
    setAccepting(true);
    setAcceptError(null);
    try {
      const ok = await authApi.acceptTerms();
      if (!ok) throw new Error('acceptance was not recorded');
      setState({ kind: 'clear' });
    } catch (e: any) {
      setAcceptError(`Could not record your acceptance: ${e?.message || e}. Please try again.`);
    } finally {
      setAccepting(false);
    }
  };

  if (state.kind === 'clear' || state.kind === 'checking') return null;

  if (state.kind === 'error') {
    return (
      <div style={overlayStyle}>
        <div style={cardStyle}>
          <h3 style={{ margin: '0 0 0.75rem', color: '#dc2626' }}>Terms check failed</h3>
          <p style={{ lineHeight: 1.5, margin: '0 0 1.1rem' }}>
            The Terms of Service acceptance check could not be completed, so this
            screen stays locked. Error: <code style={{ fontSize: '0.85em' }}>{state.message}</code>
          </p>
          <button style={buttonStyle} onClick={check}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle}>
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 0.25rem', color: '#1e293b' }}>Before you continue</h3>
        <p style={{ fontSize: '0.8rem', color: '#64748b', margin: '0 0 0.9rem' }}>
          Terms of Service &middot; version {state.version}
        </p>
        <p style={{ lineHeight: 1.5, margin: '0 0 0.75rem' }}>
          AI output is advisory. A qualified professional must review it before use.
        </p>
        <p style={{ margin: '0 0 1.1rem', fontSize: '0.85rem', color: '#64748b' }}>
          By proceeding you agree to the full{' '}
          <a href={state.termsUrl} target="_blank" rel="noopener noreferrer"
             style={{ color: '#1e293b', textDecoration: 'underline' }}>Terms of Service</a>
          {', '}
          <a href={state.baaUrl} target="_blank" rel="noopener noreferrer"
             style={{ color: '#1e293b', textDecoration: 'underline' }}>Business Associate Agreement</a>
          {' '}and{' '}
          <a href={state.privacyUrl} target="_blank" rel="noopener noreferrer"
             style={{ color: '#1e293b', textDecoration: 'underline' }}>Privacy Notice</a>.
        </p>
        {acceptError && (
          <div style={{
            border: '1px solid #dc2626', color: '#dc2626', borderRadius: 8,
            padding: '0.5rem 0.75rem', marginBottom: '0.9rem', fontSize: '0.85rem',
          }}>{acceptError}</div>
        )}
        <button style={{ ...buttonStyle, opacity: accepting ? 0.6 : 1 }} disabled={accepting} onClick={accept}>
          I agree
        </button>
      </div>
    </div>
  );
};
